import { StateCreator } from 'zustand';
import {
  generateId,
  generateRoomPartId,
  getCurrentTimestamp,
  getRandomStringSimple,
  simpleClone,
} from '@/common/utils';

import {
  AssetAlreadyExistsError,
  InvalidProjectError,
  InvalidStateError,
  MissingRoomError,
  NoSuchApplianceError,
  NoSuchAssetError,
} from '@/common/errors';

import { cloneAsset } from '@/common/projectItems';

import { BuildSlice } from '@/types/stores/build';
import { PlanSlice } from '@/types/stores/plan';
import { PlannerSlice } from '@/types/stores/planner';
import { AssetRemoveGroupInfo, ItemCreateOpts, ProjectSlice } from '@/types/stores/project';
import { RootActions } from '@/types/stores/rootActions';
import { planAction } from './storeCommon';
import {
  findAssetToRemove,
  collectHoodGroupDeletions,
  collectApplianceGroupDeletions,
  removeHoodFromGroup,
  removeApplianceFromGroup,
  removeGroups,
  removeRowsAndReindex,
  updateGroupTransformIfNeeded,
} from './project/removeAsset';
import {
  Device,
  Project,
  Hood,
  Appliance,
  Group,
  AirflowItemName,
  AirflowItem,
  Zone,
  Exhaust,
  Supply,
  VAV,
  KitchenProject,
  Room,
  Diffuser,
  DraggableDeviceId,
  DraggebleDeviceOfId,
  DraggableDeviceOfType,
  DraggableDeviceTypeName,
  DraggableDevice,
  VentilatedCeiling,
  EntityStoreMapping,
  ItemOfType,
} from '@/types/helpers';
import { createAssetInstance } from '@/common/itemUtils';
import { airchefApi } from '@/api';
import { diff } from 'datum-diff';
import {
  ApplianceId,
  ExhaustId,
  GroupId,
  HoodId,
  ProjectId,
  RowId,
  SupplyId,
  VAVId,
  ZoneId,
  DiffuserId,
  Equations,
  RoomId,
} from '@/api/airchef/Api';
import { GeneralError } from '@/components/GenErrorFloat/GenError';
import { VentilatedCeilingId } from '@/api/pim/Api';
import { getVersionFromSchema, updateAssetModified, updateProjectModified } from './project/helpers';
import { duplicateRoomAssets } from './project/duplicateRoom';
import { WritableDraft } from 'immer';
import { applyPendingWarningOperations, updateDataWithPathOperation } from '@/components/ProjectInfo/ProjectCommon';

const kitchenProjectDefaults: KitchenProject = {
  type: 'kitchen',
  altitude: {
    value: 0,
    unit: 'm',
    type: 'length',
  },
  kitchenStartTime: '07:00:00',
  calculationType: 'HoodOnly',
  energyRate: {
    type: 'power',
    unit: 'kW',
    value: 0,
  },
  daysPerWeek: 7,
  hvac: {
    schemaPath: '/schemas/v1/hvac/hvac.schema.json',
    type: 'hvac',
    roomCoolingTemperature: {
      value: 0,
      unit: 'c',
      type: 'temperature',
    },
    roomHeatingTemperature: {
      value: 0,
      unit: 'c',
      type: 'temperature',
    },
    roomCoolingRelativeHumidity: {
      value: 0,
      unit: '%',
      type: 'other',
    },
    roomHeatingRelativeHumidity: {
      value: 0,
      unit: '%',
      type: 'other',
    },
    outdoorHeatingTemperature: {
      value: 0,
      unit: 'c',
      type: 'temperature',
    },
    outdoorHeatingRelativeHumidity: {
      value: 0,
      unit: '%',
      type: 'other',
    },
    transferHeatingTemperature: {
      value: 0,
      unit: 'c',
      type: 'temperature',
    },
    transferHeatingRelativeHumidity: {
      value: 0,
      unit: '%',
      type: 'other',
    },
    transferAirflowType: {
      value: {
        value: 'pct',
        label: {
          en: 'Airfow % of hood exhaust',
        },
      },
      enum: [
        {
          value: 'Volume',
          label: {
            en: 'Airfow volume',
          },
        },
        {
          value: 'Percentage',
          label: {
            en: 'Airfow % of hood exhaust',
          },
        },
      ],
    },
    transferAirflowPct: {
      value: 0,
      unit: '%',
      type: 'other',
    },
    transferAirflowVolume: {
      value: 0,
      unit: 'm^3/h',
      type: 'volumetricFlowRate',
    },
    generalSupplyAir: {
      value: {
        value: 'CoolingAndHeating',
        label: {
          en: 'Cooling and heating',
        },
      },
      enum: [
        {
          value: 'CoolingAndHeating',
          label: {
            en: 'Cooling and heating',
          },
        },
        {
          value: 'CoolingOnly',
          label: {
            en: 'Cooling only',
          },
        },
      ],
    },
    hoodSupplyAir: {
      value: {
        value: 'Untreated',
        label: {
          en: 'Untreated',
        },
      },
      enum: [
        {
          value: 'Untreated',
          label: {
            en: 'Untreated',
          },
        },
        {
          value: 'AirConditioned',
          label: {
            en: 'Air conditioned',
          },
        },
      ],
    },
    averageSupplyCoolingTemperature: {
      value: 0,
      unit: 'c',
      type: 'temperature',
    },
    averageSupplyHeatingTemperature: {
      value: 0,
      unit: 'c',
      type: 'temperature',
    },
    averageSupplyCoolingRelativeHumidity: {
      value: 0,
      unit: '%',
      type: 'other',
    },
    hoodExhaustTemperature: {
      value: 0,
      unit: 'c',
      type: 'temperature',
    },
    hoodCapturedTemperature: {
      value: 0,
      unit: 'c',
      type: 'temperature',
    },
    recirculationGeneralVentilation: {
      value: 0,
      unit: '%',
      type: 'other',
    },
  },
  heatLoads: {
    type: 'heatload',
    schemaPath: '/schemas/v1/heatload/heatload.schema.json',
  },
};

const defaultProjectData: Project = {
  type: 'project',
  schemaPath: '/schemas/v1/project/project.schema.json',
  id: '' as ProjectId,
  airchefValues: {
    name: '',
    quoteNumber: '',
    drawingNumber: '',
    engineer: '',
    drawnBy: '',
    country: '',
    state: '',
    city: '',
    notes: '',
    datesAndAudit: {
      createdAt: '1970-01-01T00:00:00Z',
    },
    weatherCity: {
      value: {
        value: '',
        label: {
          en: '-',
        },
      },
      enum: [
        {
          value: '',
          label: {
            en: '-',
          },
        },
      ],
    },
  },
  haltonValues: kitchenProjectDefaults,
  rooms: {},
  appliances: {},
  hoods: {},
  groups: {},
  comments: [],
  supplies: {},
  exhausts: {},
  zones: {},
  vavs: {},
  diffusers: {},
  ceilings: {},
  equations: [],
  warnings: {},
};

function getEmptyProject() {
  return simpleClone(defaultProjectData);
}

export const createProjectSlice: StateCreator<
  BuildSlice & PlanSlice & PlannerSlice & ProjectSlice & RootActions, // NOSONAR
  [['zustand/devtools', never], ['zustand/immer', never]],
  [],
  ProjectSlice
> = (set, get) => ({
  project: {
    session: '',
    importing: false,
    data: getEmptyProject(),
    pendingWarningOperations: {},
    projectVersion: 'v1',
    projectSchemaVersion: '/schemas/v1',
    dataLoading: false,
    clear: () => {
      set((state) => {
        state.project.session = '';
        state.project.data = getEmptyProject();
        state.project.pendingWarningOperations = {};
        state.project.projectVersion = 'v1';
        state.project.projectSchemaVersion = '/schemas/v1';
      });
    },
    changeProjectType: (newType) => {
      const newTypeData = get().planner.projectTemplates.map[newType];
      if (!newTypeData) {
        throw new InvalidStateError(`[${newType}] is not a valid project type!`);
      }
      set(
        (state) => {
          state.project.data.schemaPath = newTypeData.schemaPath;
          state.project.data.haltonValues = simpleClone(newTypeData.haltonValues);

          const ver = getVersionFromSchema(newTypeData.schemaPath);
          state.project.projectVersion = ver;
          state.project.projectSchemaVersion = `/schemas/${ver}`;
        },
        undefined,
        { type: 'project/changeProjectType', newType },
      );
    },
    hasProject: () => {
      return !!get().project.data.id;
    },
    loadProject: async (idOrLoader) => {
      set(
        (state) => {
          state.project.dataLoading = true;
        },
        undefined,
        { type: 'project/loading', loading: true },
      );
      try {
        let project: Project | null = null;

        if (typeof idOrLoader === 'string') {
          const id = idOrLoader;
          const resp = await airchefApi.project.projectsDetail({ id });
          if (!resp.data || resp.data.id !== id) {
            throw new InvalidProjectError('Missing project data or project id mismatch!');
          }
          project = resp.data as Project;
        } else {
          project = await idOrLoader();
          if (!project) {
            return;
          }
        }
        set(
          (state) => {
            state.project.data = project;
            const ver = getVersionFromSchema(project.schemaPath);
            state.project.projectVersion = ver;
            state.project.projectSchemaVersion = `/schemas/${ver}`;
          },
          undefined,
          {
            type: 'project/loadProject',
            id: project.id,
          },
        );
      } catch (err) {
        console.error(
          `Failed to load project [${typeof idOrLoader === 'string' ? idOrLoader : 'loader'}]: ${(err as Error).message}`,
        );
        throw new InvalidProjectError((err as Error).message, { cause: err });
      } finally {
        set(
          (state) => {
            state.project.dataLoading = false;
          },
          undefined,
          { type: 'project/loading', loading: false },
        );
      }
    },
    initEmptyProject: async (onError, persistProject) => {
      const newId = generateId() as ProjectId;
      let templates = get().planner.projectTemplates.map;
      let validProjects = Object.keys(templates);
      let templateCount = validProjects.length;
      const hasTemplates = templateCount > 0;
      if (!hasTemplates) {
        // Try loading the project templates again in case the one done during page load has failed
        try {
          await get().planner.projectTemplates.load();
        } catch (err) {
          console.error('Failed to load project templates, using fallback!', err);
          onError?.(GeneralError.TEMPLATE_LOAD_FAILED);
        }
      }

      templates = get().planner.projectTemplates.map;
      validProjects = Object.keys(templates);
      templateCount = validProjects.length;

      const firstValidTemplate = templateCount > 0 ? (templates[validProjects[0]] as Project) : getEmptyProject();
      const now = getCurrentTimestamp();
      const ver = getVersionFromSchema(firstValidTemplate.schemaPath);

      set(
        (state) => {
          state.project.session = getRandomStringSimple(6);
          state.project.data = simpleClone(templates['kitchen'] ?? firstValidTemplate);
          state.project.data.id = newId;
          if (!state.project.data.airchefValues.datesAndAudit) {
            state.project.data.airchefValues.datesAndAudit = {
              createdAt: now,
            };
          }
          state.project.data.airchefValues.datesAndAudit.createdAt = now;
          state.project.data.airchefValues.datesAndAudit.modifiedAt = now;
          state.project.data.airchefValues.datesAndAudit.author = state.planner.user.data?.userPrincipalName ?? '';
          state.project.dataLoading = true;

          state.project.projectVersion = ver;
          state.project.projectSchemaVersion = `/schemas/${ver}`;
        },
        undefined,
        { type: 'project/initEmptyProject', loading: true },
      );

      try {
        const equations = await airchefApi.equations.equationsList({ schema: `/schemas/${ver}` });
        if (equations.data) {
          set(
            (state) => {
              state.project.data.equations = equations.data.filter((o) => !!o.equations) as Equations[];
            },
            undefined,
            { type: 'project/initEmptyProject/equations' },
          );
        }
      } catch (err) {
        onError?.(GeneralError.EQUATIONS_LOAD_FAILED);
        console.error('Failed to load equations', err);
      }

      try {
        await persistProject?.(get().project.data);
      } catch (err) {
        console.error('Failed to create new project', err);
        onError?.(GeneralError.SAVE_FAILED);
      } finally {
        set(
          (state) => {
            state.project.dataLoading = false;
          },
          undefined,
          { type: 'project/initEmptyProject/done', loading: false },
        );
      }

      return newId;
    },
    setAirchefValue: (key, value) => {
      set(
        (state) => {
          state.project.data.airchefValues[key] = value;
          updateProjectModified(state);
        },
        undefined,
        { type: 'project/setAirchefValue', key, value },
      );
    },
    setHaltonValue: (key, value) => {
      set(
        (state) => {
          state.project.data.haltonValues[key] = value;
          updateProjectModified(state);
        },
        undefined,
        { type: 'project/setHaltonValue', key, value },
      );
    },
    updateHaltonData: (data) => {
      set(
        (state) => {
          state.project.data.haltonValues = data;
          updateProjectModified(state);
        },
        undefined,
        { type: 'project/updateHaltonData' },
      );
    },
    addRoom: (roomData) => {
      set(
        (state) => {
          if (!roomData.id) {
            console.error('Room data must have an id');
            return;
          }
          const now = getCurrentTimestamp();
          roomData.airchefValues.datesAndAudit.author = state.planner.user.data?.userPrincipalName ?? '';
          roomData.airchefValues.datesAndAudit.modifiedBy = state.planner.user.data?.userPrincipalName ?? '';
          roomData.airchefValues.datesAndAudit.createdAt = now;
          roomData.airchefValues.datesAndAudit.modifiedAt = now;
          state.project.data.rooms[roomData.id] = roomData as Room;
          updateProjectModified(state);
        },
        undefined,
        {
          type: 'project/addRoom',
          room: roomData,
        },
      );
    },
    updateRoom: (roomData) => {
      set(
        (state) => {
          if (!roomData.id) {
            console.error('Room data must have an id');
            return;
          }

          const oldRoom = state.project.data.rooms[roomData.id];
          const existing = !!oldRoom;

          const now = getCurrentTimestamp();
          const toUpdate = simpleClone(roomData);

          if (!existing) {
            toUpdate.airchefValues.datesAndAudit.createdAt = now;
            toUpdate.airchefValues.datesAndAudit.author = state.planner.user.data?.userPrincipalName ?? '';
          } else if (oldRoom.haltonValues.paybackCalculations) {
            // Skip updating the payback calculation outputs if they exist.
            // That can be updated while the room edit is open by equation engine, but not the user.
            if (!toUpdate.haltonValues.paybackCalculations) {
              toUpdate.haltonValues.paybackCalculations = {
                type: 'payback',
                schemaPath: `/schema/${state.project.projectVersion}/payback/payback.schema.json`,
              };
            }
            toUpdate.haltonValues.paybackCalculations.outputs = oldRoom.haltonValues.paybackCalculations.outputs;
          }
          toUpdate.airchefValues.datesAndAudit.modifiedAt = now;
          toUpdate.airchefValues.datesAndAudit.modifiedBy = state.planner.user.data?.userPrincipalName ?? '';

          state.project.data.rooms[roomData.id] = toUpdate;
          updateProjectModified(state);
        },
        undefined,
        {
          type: 'project/updateRoom',
          room: roomData,
        },
      );
    },
    deleteRoom: (id) => {
      set(
        (state) => {
          if (!state.project.data.rooms[id]) {
            console.warn(`Room with id ${id} does not exist`);
            return;
          }
          for (const [groupId, group] of Object.entries(state.project.data.groups)) {
            if (group.relations.roomId === id) {
              delete state.project.data.groups[groupId as GroupId];
            }
          }

          for (const [applianceId, appliance] of Object.entries(state.project.data.appliances) as [
            ApplianceId,
            Appliance,
          ][]) {
            if (appliance.relations.roomId === id) {
              delete state.project.data.appliances[applianceId];
            }
          }

          for (const [hoodId, hood] of Object.entries(state.project.data.hoods) as [HoodId, Hood][]) {
            if (hood.relations.roomId === id) {
              // If this hood had an exhaust linked, remove that link
              if (hood.relations.exhaustId) {
                const exhaust = state.project.data.exhausts[hood.relations.exhaustId];
                if (exhaust) {
                  exhaust.relations.hoodIds = exhaust.relations.hoodIds.filter((hid) => hid !== hoodId);
                }
              }

              delete state.project.data.hoods[hoodId];
            }
          }

          for (const [vavId, vav] of Object.entries(state.project.data.vavs)) {
            if (vav.relations.roomId === id) {
              // If a supply is linked, remove that link
              if (vav.relations.supplyId) {
                const supply = state.project.data.supplies[vav.relations.supplyId];
                if (supply) {
                  supply.relations.vavIds = supply.relations.vavIds.filter((vid) => vid !== vavId);
                }
              }
              // Same with diffuser
              if (vav.relations.diffuserIds) {
                for (const diffuserId of vav.relations.diffuserIds) {
                  const diffuser = state.project.data.diffusers[diffuserId];
                  if (diffuser) {
                    diffuser.relations.vavId = undefined;
                  }
                }
              }

              delete state.project.data.vavs[vavId as VAVId];
            }
          }

          for (const zoneId of state.project.data.rooms[id].relations.zoneIds) {
            const zone = state.project.data.zones[zoneId] as Zone | undefined;
            if (zone) {
              zone.relations.roomIds = zone.relations.roomIds.filter((rid) => rid !== id);
            }
          }

          delete state.project.data.rooms[id];
          updateProjectModified(state);
        },
        undefined,
        { type: 'project/deleteRoom', roomId: id },
      );
    },
    duplicateRoom: (id, withAssets) => {
      const project = get().project;
      if (!project.data.rooms[id]) {
        console.warn(`Room with id ${id} does not exist`);
        return;
      }

      const roomToDuplicate = project.data.rooms[id];
      const newRoomId = generateRoomPartId<'room'>();
      const newRoom = {
        ...simpleClone(roomToDuplicate),
        id: newRoomId,
      };

      const user = get().planner.user.data?.userPrincipalName ?? '';
      const now = getCurrentTimestamp();
      newRoom.airchefValues.datesAndAudit.author = user;
      newRoom.airchefValues.datesAndAudit.modifiedBy = user;
      newRoom.airchefValues.datesAndAudit.createdAt = now;
      newRoom.airchefValues.datesAndAudit.modifiedAt = now;

      const { appliances, ceilings, hoods, groups, diffusers } = project.data;

      const assetData = withAssets
        ? duplicateRoomAssets(id, newRoomId, user, { appliances, ceilings, hoods, groups, diffusers })
        : ({} as ReturnType<typeof duplicateRoomAssets>);
      set(
        (state) => {
          state.project.data.rooms[newRoomId] = newRoom;
          updateProjectModified(state);

          state.project.data.appliances = {
            ...state.project.data.appliances,
            ...assetData?.appliances,
          };
          state.project.data.hoods = {
            ...state.project.data.hoods,
            ...assetData?.hoods,
          };
          state.project.data.ceilings = {
            ...state.project.data.ceilings,
            ...assetData?.ceilings,
          };
          state.project.data.diffusers = {
            ...state.project.data.diffusers,
            ...assetData?.diffusers,
          };
          state.project.data.groups = {
            ...state.project.data.groups,
            ...assetData?.groups,
          };
        },
        undefined,
        { type: 'project/duplicateRoom', roomId: id, newRoomId },
      );
    },
    getRoom: (id, projectId) => {
      const { data } = get().project;
      if (projectId !== undefined) {
        if (projectId !== data.id) {
          throw new InvalidProjectError();
        }
      }
      const room = data.rooms[id];
      if (!room) {
        throw new MissingRoomError();
      }

      return room;
    },
    getGroup: (id) => {
      const { groups } = get().project.data;

      return groups[id] as Group | undefined;
    },
    addHood: (hood) => {
      if (!hood.id) {
        hood.id = generateRoomPartId<'hood'>();
      }

      set(
        (state) => {
          if (!hood.id) {
            throw new InvalidStateError('Hood must have an id');
          }
          state.project.data.hoods[hood.id] = hood;
          updateProjectModified(state);
        },
        undefined,
        { type: 'project/addHood', hood },
      );

      return hood;
    },
    getAssets() {
      const { hoods, appliances, diffusers, ceilings } = get().project.data;

      return ([] as DraggableDevice[])
        .concat(Object.values(hoods))
        .concat(Object.values(appliances))
        .concat(Object.values(diffusers))
        .concat(Object.values(ceilings));
    },
    getAsset: <T extends DraggableDeviceId>(idOrAsset: T | DraggebleDeviceOfId<T>) => {
      if (!idOrAsset) {
        return undefined;
      }
      const id = typeof idOrAsset === 'string' ? idOrAsset : idOrAsset.id;
      const { hoods, appliances, diffusers, ceilings } = get().project.data;

      return (hoods[id as HoodId] ??
        appliances[id as ApplianceId] ??
        diffusers[id as DiffuserId] ??
        ceilings[id as VentilatedCeilingId] ??
        undefined) as DraggebleDeviceOfId<T> | undefined;
    },
    getAnything(id) {
      const { hoods, appliances, diffusers, ceilings, groups, exhausts, supplies } = get().project.data;
      return (
        hoods[id as HoodId] ??
        appliances[id as ApplianceId] ??
        diffusers[id as DiffuserId] ??
        ceilings[id as VentilatedCeilingId] ??
        groups[id as GroupId] ??
        exhausts[id as ExhaustId] ??
        supplies[id as SupplyId] ??
        undefined
      );
    },
    addAsset: (asset) => {
      if (!asset.id) {
        asset.id = generateRoomPartId();
      }

      set(
        (state) => {
          if (!asset.id) {
            throw new InvalidStateError('Asset must have an id');
          }

          asset.airchefValues.datesAndAudit.author = state.planner.user.data?.userPrincipalName ?? '';

          switch (asset.type) {
            case 'appliance':
              state.project.data.appliances[asset.id] = asset;
              break;
            case 'hood':
              state.project.data.hoods[asset.id] = asset;
              break;
            default:
              // @ts-expect-error just in case
              console.error(`Unknown asset type [${asset.type}]!`);
          }
          updateProjectModified(state);
        },
        undefined,
        { type: 'project/addAsset', appliance: asset },
      );

      return asset;
    },
    createAsset: <T extends DraggableDeviceTypeName>(
      opts: ItemCreateOpts<T>,
      isPlan?: typeof planAction,
      planActionName?: string,
    ) => {
      const baseAsset: Hood | Appliance | Diffuser | VentilatedCeiling | undefined = (() => {
        switch (opts.type) {
          case 'hood':
            return get().plan.availableHoods.list.find((a) => a.haltonValues.id === opts.itemId);
          case 'appliance':
            return get().plan.availableAppliances.list.find((a) => a.haltonValues.id === opts.itemId);
          case 'diffuser':
            return get().plan.availableDiffusers.list.find((d) => d.airchefValues.id === opts.itemId);
          case 'ceiling':
            return get().plan.availableCeilings.list.find((c) => c.airchefValues.id === opts.itemId);
          default:
            throw new InvalidStateError(`Unknown asset type ${opts.type satisfies never} to create!`);
        }
      })();
      if (!baseAsset) {
        throw new NoSuchApplianceError(`No appliance found with id [${opts.itemId}]`);
      }

      const assetInstance = createAssetInstance(baseAsset, opts);

      if (!assetInstance) {
        throw new InvalidStateError(`Could not create new asset`);
      }

      const user = get().planner.user.data?.userPrincipalName ?? '';
      assetInstance.airchefValues.datesAndAudit.author = user;
      assetInstance.airchefValues.datesAndAudit.modifiedBy = user;

      set(
        (state) => {
          switch (assetInstance.type) {
            case 'appliance':
              state.project.data.appliances[assetInstance.id] = assetInstance;
              break;
            case 'hood':
              state.project.data.hoods[assetInstance.id] = assetInstance;
              break;
            case 'diffuser':
              state.project.data.diffusers[assetInstance.id] = assetInstance;
              break;
            case 'ceiling':
              state.project.data.ceilings[assetInstance.id] = assetInstance;
              break;
            default: {
              const { type } = assetInstance satisfies never;
              console.error(`Unknown asset type [${type}]!`);
            }
          }
          updateProjectModified(state);
        },
        undefined,
        {
          type: getActionType('createAsset', isPlan, planActionName),
          itemType: opts.type,
          itemId: opts.itemId,
          inRoom: opts.inRoom,
          roomId: opts.inRoom ? opts.roomId : undefined,
        },
      );

      return get().project.getAsset(assetInstance.id) as DraggableDeviceOfType<T> | undefined;
    },
    updateGroup: (group, isPlan, planActionName) => {
      const oldGroup = simpleClone(get().project.data.groups[group.id]);
      const updatedGroup = {
        ...oldGroup,
        ...group,
      };

      const groupDiff = diff(oldGroup, updatedGroup, {
        prefilter(_path, key) {
          if (key === 'datesAndAudit') return true;
          return false;
        },
      });
      if (!groupDiff || groupDiff.length < 0) {
        return;
      }

      set(
        (state) => {
          state.project.data.groups[group.id] = updatedGroup;
          updateProjectModified(state);
        },
        undefined,
        {
          type: getActionType('updateGroup', isPlan, planActionName),
          group,
        },
      );
    },
    removeGroup: (id, deleteAppliances, deleteHoods, isPlan, planActionName) => {
      set(
        (state) => {
          const group = state.project.data.groups[id];

          if (deleteAppliances && group) {
            // Delete all appliances under the group
            group.relations.applianceIds?.forEach((aid) => {
              delete state.project.data.appliances[aid];
            });
          } else if (group) {
            // We're removing the group, but not appliances.
            // still need to remove the group from appliances
            group.relations.applianceIds?.forEach((aid) => {
              const appliance = state.project.data.appliances[aid];

              if (appliance) appliance.relations.groupId = undefined;
            });
          }

          if (group?.relations.hoodIds && deleteHoods) {
            // This group had hoods, remove the hoods as well
            group.relations.hoodIds.forEach((hoodId) => {
              delete state.project.data.hoods[hoodId];
            });
          }

          delete state.project.data.groups[id];
          updateProjectModified(state);
        },
        undefined,
        {
          type: getActionType('removeGroup', isPlan, planActionName),
          id,
          deleteAppliances,
          deleteHoods,
        },
      );
    },
    removeGroupRow: (groupId, rowIdOrNum, isPlan, planActionName) => {
      set(
        (state) => {
          const group = state.project.data.groups[groupId];
          const row =
            typeof rowIdOrNum === 'number' ? group?.rows[rowIdOrNum] : group?.rows.find((r) => r.id === rowIdOrNum);
          const rowIndex = row && group ? group.rows.indexOf(row) : -1;
          const onlyRow = group?.rows.length === 1;

          if (!group || !row) return;
          // Delete all appliances in the row
          const floatingAppliances = row.relations.floatingApplianceIds.map((ap) => ap.applianceId);
          group.relations.applianceIds = group.relations.applianceIds.filter(
            (aid) => !row.relations.applianceIds.includes(aid) && !floatingAppliances.includes(aid),
          );
          row.relations.applianceIds.forEach((aid) => {
            delete state.project.data.appliances[aid];
          });
          floatingAppliances.forEach((aid) => {
            delete state.project.data.appliances[aid];
          });

          // delete all hoods in the row
          group.relations.hoodIds = group.relations.hoodIds.filter((hid) => !row.relations.hoodIds.includes(hid));
          row.relations.hoodIds.forEach((hid) => {
            delete state.project.data.hoods[hid];
          });

          if (onlyRow) {
            // Group won't have any more rows after this is deleted, remove the whole group
            delete state.project.data.groups[groupId];
            return;
          }
          // Group has rows remaining, update the group, rows & appliances as needed

          // @ts-expect-error we do not know the actual resulting count of rows here
          group.rows = group.rows.filter((r) => r?.id !== row.id);
          if (rowIndex === 0) {
            // First row was removed, update the group position to the new first row
            group.airchefValues.transform =
              state.project.data.appliances[group.rows[0].relations.applianceIds[0]].airchefValues.transform;
          }

          // Update all remaining appliance & hood row indices
          for (let newRowIndex = 0; newRowIndex < group.rows.length; newRowIndex++) {
            const r = group.rows[newRowIndex];
            for (const aid of r.relations.applianceIds) {
              state.project.data.appliances[aid].relations.rowNum = newRowIndex;
            }

            for (const hid of r.relations.hoodIds) {
              state.project.data.hoods[hid].relations.rowNum = newRowIndex;
            }
          }
        },
        undefined,
        { type: getActionType('removeGroupRow', isPlan, planActionName), groupId, row: rowIdOrNum },
      );
    },
    cleanupRowSpacers: (groupId, rowNum, position = 'both') => {
      const cleanFirst = position === 'both' || position === 'start';
      const cleanLast = position === 'both' || position === 'end';
      set(
        (state) => {
          const group = state.project.data.groups[groupId];

          if (!group) return;

          const row = group.rows[rowNum];

          if (!row) return;

          const applianceCount = row.relations.applianceIds.length;
          if (applianceCount < 1) return;
          const lastApplianceIdx = applianceCount - 1;

          const toRemove: ApplianceId[] = [];

          if (
            cleanFirst &&
            state.project.data.appliances[row.relations.applianceIds[0]].haltonValues.type === 'spacer'
          ) {
            // If the first appliance in the row is a spacer, mark for deletion
            toRemove.push(row.relations.applianceIds[0]);
          }
          if (
            cleanLast &&
            lastApplianceIdx > 0 &&
            state.project.data.appliances[row.relations.applianceIds[lastApplianceIdx]].haltonValues.type === 'spacer'
          ) {
            // If there is more than one appliance, then check the same for the last appliance
            toRemove.push(row.relations.applianceIds[lastApplianceIdx]);
          }

          // Update the group relations to remove the spacers
          row.relations.applianceIds = row.relations.applianceIds.filter((aid) => !toRemove.includes(aid));
          group.relations.applianceIds = group.relations.applianceIds.filter((aid) => !toRemove.includes(aid));

          // Actually delete the spacers
          for (const aid of toRemove) {
            delete state.project.data.appliances[aid];
          }
        },
        undefined,
        { type: 'project/cleanupGroupSpacers', groupId },
      );
    },
    addToHood: async (appliance: ApplianceId | Appliance, hoodId, isPlan, planActionName) => {
      const { project, plan } = get();
      const hood = project.data.hoods[hoodId];
      if (!hood || hood.type !== 'hood') {
        throw new NoSuchAssetError('hood', `No hood found with id ${hoodId}`);
      }
      const applianceId = typeof appliance === 'string' ? appliance : appliance.id;
      const applianceAsset = typeof appliance === 'string' ? project.getAsset(applianceId) : appliance;

      if (!applianceAsset) {
        throw new NoSuchAssetError('appliance', `No appliance found with id ${applianceId}`);
      }

      if (!applianceAsset.relations.roomId) {
        throw new InvalidStateError('Cannot add an appliance without a room to a group / hood');
      }

      const baseGroup = await plan.availableGroup.get();

      set(
        (state) => {
          let group: Group | undefined;
          if (hood.relations.groupId) {
            group = state.project.data.groups[hood.relations.groupId];
          }

          if (group) {
            group.relations.applianceIds.push(applianceId);
            if (!group.relations.hoodIds.includes(hood.id)) group.relations.hoodIds.push(hood.id);
          } else {
            group = baseGroup;
            group.id = generateRoomPartId();
            group.relations = {
              roomId: applianceAsset.relations.roomId!,
              applianceIds: [applianceId],
              hoodIds: [hood.id],
            };
            state.project.data.groups[group.id] = group;
            const writeableHood = state.project.data.hoods[hood.id];
            writeableHood.relations.groupId = group.id;
          }

          const writeableAppliance = state.project.data.appliances[applianceId];
          writeableAppliance.relations.groupId = group.id;
          updateProjectModified(state);
        },
        undefined,
        { type: getActionType('addToHood', isPlan, planActionName), appliance, hoodId },
      );
    },
    addGroupToHood: (group, hoodId, isPlan, planActionName) => {
      const groupId = typeof group === 'string' ? group : group.id;

      set(
        (state) => {
          const writableGroup = state.project.data.groups[groupId];
          if (!writableGroup) {
            throw new NoSuchAssetError(`No group found with id ${groupId}`);
          }

          const writableHood = state.project.data.hoods[hoodId];
          if (!writableHood || writableHood.type !== 'hood') {
            throw new NoSuchAssetError('hood', `No hood found with id ${hoodId}`);
          }

          if (!writableHood.relations.groupId) {
            // No group under hood, add this directly
            writableHood.relations.groupId = groupId;
            writableGroup.relations.hoodIds.push(hoodId);
          } else {
            // Hood already has a group, move the appliances to that group, merging the groups
            const existingGroup = state.project.data.groups[writableHood.relations.groupId];
            if (!existingGroup) {
              throw new NoSuchAssetError('group', `No group found with id ${writableHood.relations.groupId}`);
            }

            // Add the appliance IDs to the existing group
            existingGroup.relations.applianceIds.push(...writableGroup.relations.applianceIds);
            // Update the group relation on the appliances
            writableGroup.relations.applianceIds.forEach((aid) => {
              state.project.data.appliances[aid].relations.groupId = existingGroup.id;
            });

            // Remove the extra group
            delete state.project.data.groups[writableGroup.id];

            updateProjectModified(state);
          }
        },
        undefined,
        {
          type: getActionType('addGroupToHood', isPlan, planActionName),
          group,
          hoodId,
        },
      );
    },

    updateAsset: (asset, isPlan, planActionName) => {
      const { hoods, appliances, diffusers, ceilings } = get().project.data;

      if (
        !hoods[asset.id as HoodId] &&
        !appliances[asset.id as ApplianceId] &&
        !diffusers[asset.id as DiffuserId] &&
        !ceilings[asset.id as VentilatedCeilingId]
      ) {
        throw new NoSuchAssetError(asset.type, `No asset found with id ${asset.id}`);
      }

      const assetMap =
        asset.type === 'appliance'
          ? 'appliances'
          : asset.type === 'diffuser'
            ? 'diffusers'
            : asset.type === 'ceiling'
              ? 'ceilings'
              : 'hoods';
      set(
        (state) => {
          // @ts-expect-error can be either hood or appliance, types differ, but this goes to the correct place
          state.project.data[assetMap][asset.id] = {
            ...asset,
            airchefValues: {
              ...asset.airchefValues,
              datesAndAudit: {
                ...asset.airchefValues.datesAndAudit,
                modifiedAt: getCurrentTimestamp(),
                modifiedBy: state.planner.user.data?.userPrincipalName ?? '',
              },
            },
          };

          state.plan.dirty = true;

          updateProjectModified(state);
        },
        undefined,
        {
          type: getActionType('updateAsset', isPlan, planActionName),
          asset,
          assetType: asset.type,
        },
      );
    },
    updateAssets(assetsToUpdate, isPlan, planActionName) {
      const assetsToUpdateIds = new Set(assetsToUpdate.map((a) => a.id));
      const now = getCurrentTimestamp();
      set(
        (state) => {
          assetsToUpdate.forEach((asset) => {
            const assetMap =
              asset.type === 'appliance'
                ? 'appliances'
                : asset.type === 'diffuser'
                  ? 'diffusers'
                  : asset.type === 'ceiling'
                    ? 'ceilings'
                    : 'hoods';
            // @ts-expect-error can be either hood or appliance, types differ, but this goes to the correct place
            state.project.data[assetMap][asset.id] = {
              ...asset,
              airchefValues: {
                ...asset.airchefValues,
                datesAndAudit: {
                  ...asset.airchefValues.datesAndAudit,
                  modifiedAt: now,
                  modifiedBy: state.planner.user.data?.userPrincipalName ?? '',
                },
              },
            };
          });

          state.plan.dirty = true;

          updateProjectModified(state);
        },
        undefined,
        {
          type: getActionType('updateAssets', isPlan, planActionName),
          assets: assetsToUpdateIds,
        },
      );

      const { hoods, appliances } = get().project.data;
      const updatedAssets: Device[] = Array.from(assetsToUpdateIds)
        .map((id) => {
          // @ts-expect-error different id types
          return hoods[id] ?? appliances[id] ?? undefined;
        })
        .filter((v) => v);

      return updatedAssets;
    },
    updateAssetRelations: (operations) => {
      if (!operations || operations.length === 0) return;

      const modifiedEntities = new Map<
        HoodId | RoomId | ZoneId | ApplianceId | VAVId | SupplyId | ExhaustId | GroupId | DiffuserId,
        WritableDraft<Hood | Room | Zone | Appliance | VAV | Supply | Exhaust | Group | Diffuser>
      >();

      set(
        (state) => {
          for (const operation of operations) {
            const targetStore = state.project.data[entityStoreMap[operation.kind]];
            if (!targetStore) {
              console.warn(`Could not find store for entity type [${operation.kind}]`);
              continue;
            }

            // @ts-expect-error multiple id possibilities
            const targetEntity = targetStore[operation.id] as
              | WritableDraft<ItemOfType<typeof operation.kind>>
              | undefined;
            if (!targetEntity) {
              console.warn(`Could not find asset of type [${operation.kind}] with id [${operation.id}]`);
              continue;
            }
            if (operation.op === 'add') {
              if (operation.multiple) {
                // @ts-expect-error too generic
                if (!targetEntity.relations[operation.relName]) {
                  // @ts-expect-error too generic
                  targetEntity.relations[operation.relName] = [] as string[];
                }
                // @ts-expect-error too generic
                targetEntity.relations[operation.relName].push(operation.targetId);
              } else {
                // @ts-expect-error too generic
                targetEntity.relations[operation.relName] = operation.targetId;
              }
            } else if (operation.op === 'rem') {
              if (operation.multiple) {
                // @ts-expect-error too generic
                if (!targetEntity.relations[operation.relName]) {
                  // @ts-expect-error too generic
                  targetEntity.relations[operation.relName] = [] as string[];
                }
                // @ts-expect-error too generic
                targetEntity.relations[operation.relName] = targetEntity.relations[operation.relName].filter(
                  (v: string) => v !== operation.targetId,
                );
              } else {
                // @ts-expect-error too generic
                delete targetEntity.relations[operation.relName];
              }
            }
            modifiedEntities.set(targetEntity.id, targetEntity);
          }

          for (const [, entity] of modifiedEntities) {
            if (entity.type === 'group') continue;

            updateAssetModified(
              entity as WritableDraft<Hood | Room | Zone | Appliance | VAV | Supply | Exhaust>,
              state,
            );
          }
        },
        undefined,
        { type: 'project/updateAssetRelations', operations },
      );
    },
    removeAsset: (asset, isPlan, planActionName) => {
      const id = typeof asset === 'string' ? asset : asset.id;
      const { groups, hoods, appliances, diffusers, ceilings } = get().project.data;

      const foundAsset = findAssetToRemove(id, { appliances, hoods, diffusers, ceilings });

      if (!foundAsset) {
        console.warn('Could not find asset to delete!', id);
        return {};
      }

      let assetsToBeRemoved = [foundAsset.id];
      let groupsToBeRemoved: GroupId[] = [];
      let rowsToBeRemoved: RowId[] = [];
      let groupInfo: AssetRemoveGroupInfo | undefined;
      let rowNum = -1;
      let firstRowId: RowId | undefined;

      // Collect deletions based on asset type
      if (foundAsset.type === 'hood') {
        assetsToBeRemoved = collectHoodGroupDeletions(foundAsset, groups);
      } else if (foundAsset.type === 'appliance' && foundAsset.relations.groupId) {
        const deletions = collectApplianceGroupDeletions(foundAsset, groups, appliances);
        groupsToBeRemoved = deletions.groupsToRemove;
        rowsToBeRemoved = deletions.rowsToRemove;
        rowNum = deletions.rowNum;
        firstRowId = deletions.firstRowId;
        groupInfo = deletions.groupInfo;
        // Add spacers to the removal list
        assetsToBeRemoved.push(...deletions.spacersToRemove);
      }

      set(
        (state) => {
          function unlinkHood(hid: HoodId) {
            const hood = state.project.data.hoods[hid] as Hood | undefined;
            if (hood?.relations.exhaustId) {
              const exhaust = state.project.data.exhausts[hood.relations.exhaustId];
              if (exhaust) {
                exhaust.relations.hoodIds = exhaust.relations.hoodIds.filter((ehid) => ehid !== hid);
              }
            }
          }

          // Remove hood from its group
          if (foundAsset.type === 'hood') {
            if (foundAsset.relations.groupId) {
              removeHoodFromGroup(state, assetsToBeRemoved as HoodId[], foundAsset.relations.groupId);
            }
            unlinkHood(foundAsset.id);
          }
          // Delete assets
          assetsToBeRemoved.forEach((aid) => {
            unlinkHood(aid as HoodId);
            delete state.project.data.hoods[aid as HoodId];
            delete state.project.data.appliances[aid as ApplianceId];
            delete state.project.data.diffusers[aid as DiffuserId];
            delete state.project.data.ceilings[aid as VentilatedCeilingId];
          });

          // Remove appliance from its group
          if (foundAsset.type === 'appliance' && foundAsset.relations.groupId) {
            removeApplianceFromGroup(state, foundAsset, rowNum);
          }

          // Remove groups
          removeGroups(state, groupsToBeRemoved);

          if (foundAsset.type !== 'diffuser' && foundAsset.type !== 'ceiling') {
            // Remove rows and reindex
            if (rowsToBeRemoved.length > 0 && foundAsset.relations.groupId) {
              removeRowsAndReindex(state, foundAsset.relations.groupId, rowsToBeRemoved);
            }

            // Update group transform if first row changed
            if (foundAsset.relations.groupId) {
              updateGroupTransformIfNeeded(state, foundAsset.relations.groupId, firstRowId);
            }
          }

          state.plan.dirty = true;
          updateProjectModified(state);
        },
        undefined,
        {
          type: getActionType('removeAsset', isPlan, planActionName),
          appliance: id,
        },
      );

      return { device: foundAsset, groupInfo };
    },
    duplicateAsset: async (id, createGroup, isPlan, planActionName) => {
      const { hoods, appliances, groups } = get().project.data;
      // @ts-expect-error different id types
      const asset: Device | undefined = (hoods[id] ?? appliances[id] ?? undefined) as Device | undefined;
      if (!asset) {
        return {
          newAssets: [],
          updatedAssets: [],
        };
      }

      const emptyGroup = await get().plan.availableGroup.get();

      const assetList = ([] as Device[]).concat(Object.values(hoods)).concat(Object.values(appliances));

      const cloneResult = cloneAsset(asset, assetList, Object.values(groups), !!createGroup, emptyGroup);

      const updatedIds = cloneResult.updatedAssets.map((a) => a.id);

      // Return list of IDs of the new appliances, and the original unmodified appliances for the updated ones
      const ret = {
        newAssets: cloneResult.updatedAssets.map((a) => a.id),
        updatedAssets: assetList.filter((a) => updatedIds.includes(a.id)),
      };

      set(
        (state) => {
          cloneResult.newAssets.forEach((a) => {
            switch (a.type) {
              case 'appliance':
                state.project.data.appliances[a.id] = a;
                break;
              case 'hood':
                state.project.data.hoods[a.id] = a;
                break;
            }
          });

          cloneResult.updatedAssets.forEach((a) => {
            const assetMap = a.type === 'appliance' ? 'appliances' : 'hoods';
            // @ts-expect-error different id types
            if (state.project.data[assetMap][a.id]) {
              // @ts-expect-error different id types
              state.project.data[assetMap][a.id] = a;
            }
          });
          cloneResult.updatedGroups.forEach((g) => {
            state.project.data.groups[g.id] = g;
          });

          state.plan.dirty = true;

          updateProjectModified(state);
        },
        undefined,
        { type: getActionType('duplicateAsset', isPlan, planActionName), applianceId: id },
      );

      return ret;
    },
    updateProject: async (updateCallback, isSystem) => {
      const project = get().project.data;

      const clone = simpleClone(project);

      const updated = await Promise.resolve(updateCallback(clone));

      if (!updated) {
        return;
      }

      set(
        (state) => {
          state.project.data = updated;

          updateProjectModified(state, isSystem);
        },
        undefined,
        { type: 'project/updateProject' },
      );
    },
    updateProjectDirect: (updateCallback, isSystem) => {
      set(
        (state) => {
          updateCallback(state.project.data);
          updateProjectModified(state, isSystem);
        },
        undefined,
        { type: 'project/updateProjectDirect' },
      );
    },
    stageWarningOperations: (drawerId, operations) => {
      set(
        (state) => {
          const pending = state.project.pendingWarningOperations;
          pending[drawerId] ??= [];
          pending[drawerId].push(...operations);
        },
        undefined,
        { type: 'project/stageWarningOperations', drawerId, operations },
      );
    },
    commitWarningOperations: (drawerId) => {
      const operations = get().project.pendingWarningOperations[drawerId];
      if (!operations) return;
      set(
        (state) => {
          for (const output of operations) {
            updateDataWithPathOperation(state.project.data, output.jsonPath, output.operation, output.value);
          }
          delete state.project.pendingWarningOperations[drawerId];
          updateProjectModified(state, true);
        },
        undefined,
        { type: 'project/commitWarningOperations', drawerId },
      );
    },
    clearWarningOperations: (drawerId) => {
      if (!get().project.pendingWarningOperations[drawerId]) return;
      set(
        (state) => {
          delete state.project.pendingWarningOperations[drawerId];
        },
        undefined,
        { type: 'project/clearWarningOperations', drawerId },
      );
    },
    getProject: () => {
      return get().project.data;
    },
    getProjectWithPendingWarnings: () => {
      const { data, pendingWarningOperations } = get().project;
      const warnings = applyPendingWarningOperations(data.warnings, pendingWarningOperations);
      return warnings === data.warnings ? data : { ...data, warnings };
    },
    setProject: (project) => {
      set(
        (state) => {
          state.project.data = project;
        },
        undefined,
        { type: 'project/setProject' },
      );
    },
    assetCleanup: () => {
      // This removes any hoods & appliances without airchefvalues, incase already removed one has been updated through ws.
      set(
        (state) => {
          for (const hid of Object.keys(state.project.data.hoods) as HoodId[]) {
            if (!state.project.data.hoods[hid].airchefValues) {
              delete state.project.data.hoods[hid];
            }
          }
          for (const aid of Object.keys(state.project.data.appliances) as ApplianceId[]) {
            if (!state.project.data.appliances[aid].airchefValues) {
              delete state.project.data.appliances[aid];
            }
          }
        },
        undefined,
        { type: 'project/assetCleanup' },
      );
    },
    airflow: {
      addItem: async (item, linkToZone) => {
        const storeName = getAirflowItemStore(item.type);
        const { data } = get().project;

        // @ts-expect-error different id types
        if (data[storeName][item.id]) {
          throw new AssetAlreadyExistsError(item.type, 'Could not add airflow item');
        }
        const isDevice = item.type === 'exhaust' || item.type === 'supply' || item.type === 'vav';
        let zoneToLink: Zone | undefined;
        if (isDevice) {
          if (linkToZone === true) {
            zoneToLink = await get().plan.availableZone.quickCreate();
          } else if (typeof linkToZone === 'string') {
            zoneToLink = get().project.data.zones[linkToZone];
          }
        }

        const now = getCurrentTimestamp();
        set(
          (state) => {
            const partialRelations = (() => {
              if (isDevice && zoneToLink) {
                if (item.type === 'vav') {
                  return {
                    zoneId: zoneToLink.id,
                  };
                } else {
                  return {
                    zoneIds: [zoneToLink.id],
                  };
                }
              }

              return {};
            })();

            // @ts-expect-error different id types
            state.project.data[storeName][item.id] = {
              ...item,
              airchefValues: {
                ...item.airchefValues,
                datesAndAudit: {
                  author: state.planner.user.data?.userPrincipalName ?? '',
                  modifiedBy: state.planner.user.data?.userPrincipalName ?? '',
                  createdAt: now,
                  modifiedAt: now,
                },
              },
              relations: {
                ...item.relations,
                ...partialRelations,
              },
            };

            if (isDevice && zoneToLink) {
              if (item.type !== 'vav') {
                if (item.type === 'exhaust') {
                  state.project.data.zones[zoneToLink.id].relations.exhaustIds.push(item.id);
                } else if (item.type === 'supply') {
                  state.project.data.zones[zoneToLink.id].relations.supplyIds.push(item.id);
                }
              }
            }

            if (item.type) updateProjectModified(state);
          },
          undefined,
          { type: 'project/airflow/addItem', item },
        );

        return simpleClone(item);
      },
      updateItem: (item) => {
        const storeName = getAirflowItemStore(item.type);
        const { data } = get().project;

        // @ts-expect-error different id types
        if (!data[storeName][item.id]) {
          throw new NoSuchAssetError(item.type, 'Could not update airflow item');
        }

        const now = getCurrentTimestamp();

        set(
          (state) => {
            // deep merge here?
            // @ts-expect-error different id types
            state.project.data[storeName][item.id] = {
              ...item,
              airchefValues: {
                ...item.airchefValues,
                datesAndAudit: {
                  ...item.airchefValues.datesAndAudit,
                  createdAt: now,
                  modifiedAt: now,
                },
              },
            };
            updateProjectModified(state);
          },
          undefined,
          { type: 'project/airflow/updateItem', item },
        );
      },
      removeItem: <T extends AirflowItem>(itemOrId: T | ZoneId | SupplyId | ExhaustId | VAVId) => {
        const id = typeof itemOrId === 'string' ? itemOrId : itemOrId.id;
        const { data } = get().project;
        // Nullish coalescing seems to collapse the types only to the leftmost one, here as Zone
        const foundItem: Zone | Exhaust | Supply | VAV | undefined =
          // @ts-expect-error different id types
          data.zones[id] ?? data.supplies[id] ?? data.exhausts[id] ?? data.vavs[id];
        // so cast it to the correct type union
        const oldItem = foundItem as Zone | Exhaust | Supply | VAV | undefined; // NOSONAR

        if (!oldItem) {
          return null;
        }

        const storeName = getAirflowItemStore(oldItem.type);
        set(
          (state) => {
            // If this is a zone, unlink any exhausts, supplies and rooms from it
            if (oldItem.type === 'zone') {
              for (const eid of oldItem.relations.exhaustIds) {
                state.project.data.exhausts[eid].relations.zoneIds = state.project.data.exhausts[
                  eid
                ].relations.zoneIds.filter((zid) => zid !== oldItem.id);
              }
              for (const sid of oldItem.relations.supplyIds) {
                state.project.data.supplies[sid].relations.zoneIds = state.project.data.supplies[
                  sid
                ].relations.zoneIds.filter((zid) => zid !== oldItem.id);
              }
              for (const rid of oldItem.relations.roomIds) {
                state.project.data.rooms[rid].relations.zoneIds = state.project.data.rooms[
                  rid
                ].relations.zoneIds.filter((zid) => zid !== oldItem.id);
              }

              for (const vav of Object.values(state.project.data.vavs)) {
                if (vav.relations.zoneId === oldItem.id) {
                  vav.relations.zoneId = undefined;
                }
              }
            }

            // If supply, unlink any VAVs
            if (oldItem.type === 'supply') {
              for (const vid of oldItem.relations.vavIds) {
                state.project.data.vavs[vid].relations.supplyId = undefined;
              }
            }

            // If exhaust, unlink any hoods
            if (oldItem.type === 'exhaust') {
              for (const hid of oldItem.relations.hoodIds) {
                state.project.data.hoods[hid].relations.exhaustId = undefined;
              }
            }

            // If VAV, unlink supply & diffuser
            if (oldItem.type === 'vav') {
              if (oldItem.relations.supplyId) {
                const supplyVavs = state.project.data.supplies[oldItem.relations.supplyId].relations.vavIds;
                state.project.data.supplies[oldItem.relations.supplyId].relations.vavIds = supplyVavs.filter(
                  (vid) => vid !== oldItem.id,
                );
              }

              for (const did of oldItem.relations.diffuserIds || []) {
                state.project.data.diffusers[did].relations.vavId = undefined;
              }
            }

            // If exhaust or supply, unlink zones
            if (oldItem.type === 'supply' || oldItem.type === 'exhaust') {
              for (const zid of oldItem.relations.zoneIds) {
                state.project.data.zones[zid].relations.supplyIds = state.project.data.zones[
                  zid
                ].relations.supplyIds.filter((sid) => sid !== oldItem.id);

                state.project.data.zones[zid].relations.exhaustIds = state.project.data.zones[
                  zid
                ].relations.exhaustIds.filter((eid) => eid !== oldItem.id);
              }
            }

            // @ts-expect-error different id types
            delete state.project.data[storeName][id];
          },
          undefined,
          { type: 'project/airflow/removeItem', item: itemOrId },
        );

        return simpleClone(oldItem) as T;
      },
      changeItemZone: (item, zoneId, addOrRemove) => {
        const { supplies, exhausts, zones } = get().project.data;

        const foundItem: Supply | Exhaust | undefined = supplies[item.id as SupplyId] ?? exhausts[item.id as ExhaustId];
        if (!foundItem) {
          throw new NoSuchAssetError(item.type, 'Could not find airflow item in store');
        }
        const foundZone = zones[zoneId];
        if (!foundZone) {
          throw new NoSuchAssetError('zone', 'Could not find zone to add/remove items for');
        }

        const store = getAirflowItemStore(item.type) as 'supplies' | 'exhausts';
        const zoneRelation = `${item.type}Ids` as 'exhaustIds' | 'supplyIds';

        set(
          (state) => {
            if (!addOrRemove) {
              // No operation specified, toggle
              if (foundItem.relations.zoneIds.includes(zoneId)) {
                // Item is in this zone, remove
                addOrRemove = 'remove';
              } else {
                // Item is not in this zone, add
                addOrRemove = 'add';
              }
            }

            if (addOrRemove === 'add') {
              // @ts-expect-error different id types on different relations
              state.project.data[store][item.id].relations.zoneIds.push(zoneId);
              // @ts-expect-error different id types on different relations
              state.project.data.zones[zoneId].relations[zoneRelation].push(item.id);
            } else if (addOrRemove === 'remove') {
              // @ts-expect-error different id types on different relations
              state.project.data[store][item.id].relations.zoneIds = state.project.data[store][
                item.id
                // @ts-expect-error different id types on different relations
              ].relations.zoneIds.filter((zid) => zid !== zoneId);

              // @ts-expect-error different id types on different relations
              state.project.data.zones[zoneId].relations[zoneRelation] = state.project.data.zones[zoneId].relations[
                zoneRelation
              ].filter((iid) => iid !== item.id);
            }

            // @ts-expect-error issues with union typing here
            updateAssetModified(state.project.data[store][item.id], state);
            updateAssetModified(state.project.data.zones[zoneId], state);
            updateProjectModified(state);
          },
          undefined,
          { type: 'project/airflow/changeItemZone', item, zoneId, addOrRemove },
        );
      },
      changeRoomZone(baseRoom, zoneId, action) {
        const { rooms, zones } = get().project.data;

        const foundRoom = rooms[baseRoom.id];
        if (!foundRoom) {
          throw new NoSuchAssetError('room', 'Could not find room in store');
        }
        const foundZone = zones[zoneId];
        if (!foundZone) {
          throw new NoSuchAssetError('zone', 'Could not find zone to add/remove items for');
        }

        if (!action) {
          if ((foundRoom.relations.zoneIds ?? []).includes(zoneId)) {
            action = 'remove';
          } else {
            action = 'add';
          }
        }

        set(
          (state) => {
            const room = state.project.data.rooms[baseRoom.id];
            const zone = state.project.data.zones[zoneId];
            if (!room.relations.zoneIds) {
              room.relations.zoneIds = [];
            }
            if (action === 'add') {
              room.relations.zoneIds.push(zoneId);
              zone.relations.roomIds.push(room.id);
            } else if (action === 'remove') {
              room.relations.zoneIds = room.relations.zoneIds.filter((zid) => zid !== zoneId);
              zone.relations.roomIds = zone.relations.roomIds.filter((rid) => rid !== baseRoom.id);
            }

            updateAssetModified(room, state);
            updateAssetModified(zone, state);
            updateProjectModified(state);
          },
          undefined,
          { type: 'project/airflow/changeRoomZone', room: baseRoom, zoneId, addOrRemove: action },
        );
      },
      changeVavZone(vav, zoneId) {
        const { vavs } = get().project.data;
        const foundVav = vavs[vav.id];

        if (!foundVav) {
          throw new NoSuchAssetError('vav', 'Could not find vav in store');
        }
        const remove = foundVav.relations.zoneId === zoneId;
        set(
          (state) => {
            const v = state.project.data.vavs[foundVav.id];

            if (remove) {
              v.relations.zoneId = undefined;
            } else {
              v.relations.zoneId = zoneId;
            }
          },
          undefined,
          { type: 'project/airflow/changeVavZone', vav: foundVav, zoneId, remove },
        );
      },
      getItem<T extends 'supply' | 'exhaust'>(
        type: T,
        zoneId: ZoneId | undefined,
        fallback: 'none' | 'all',
      ): T extends 'supply' ? Supply[] : Exhaust[] {
        const items =
          type === 'supply' ? Object.values(get().project.data.supplies) : Object.values(get().project.data.exhausts);

        if (!zoneId) {
          if (fallback === 'none') return [];
          else return items as T extends 'supply' ? Supply[] : Exhaust[];
        }

        return items.filter((i) => i.relations.zoneIds.includes(zoneId)) as T extends 'supply' ? Supply[] : Exhaust[];
      },
      linkHoodToExhaust: (exhaustId, hoodId) => {
        const { hoods, exhausts } = get().project.data;

        const hood = hoods[hoodId] as Hood | undefined;
        const exhaust = exhausts[exhaustId] as Exhaust | undefined;

        if (!hood) {
          throw new NoSuchAssetError('hood', 'Can not link nonexisting hood to exhaust');
        }

        if (!exhaust) {
          throw new NoSuchAssetError('exhaust', 'Can not link hood to nonexisting exhaust');
        }

        if (hood.relations.exhaustId && hood.relations.exhaustId !== exhaustId) {
          throw new InvalidStateError('Hood is already linked to another exhaust!');
        }

        set(
          (state) => {
            state.project.data.hoods[hoodId].relations.exhaustId = exhaustId;
            if (!state.project.data.exhausts[exhaustId].relations.hoodIds.includes(hoodId)) {
              state.project.data.exhausts[exhaustId].relations.hoodIds.push(hoodId);
            }

            updateAssetModified(state.project.data.hoods[hoodId], state);
            updateAssetModified(state.project.data.exhausts[exhaustId], state);
          },
          undefined,
          { type: 'project/airflow/linkHoodToExhaust', exhaustId, hoodId },
        );
      },
      unlinkHoodExhaust: (exhaustId, hoodId) => {
        const { hoods, exhausts } = get().project.data;

        const hood = hoods[hoodId] as Hood | undefined;
        const exhaust = exhausts[exhaustId] as Exhaust | undefined;

        if (!hood) {
          throw new NoSuchAssetError('hood', 'Can not unlink nonexisting hood from exhaust');
        }

        if (!exhaust) {
          throw new NoSuchAssetError('exhaust', 'Can not unlink hood from nonexisting exhaust');
        }

        if (hood.relations.exhaustId !== exhaustId) {
          throw new InvalidStateError('Attempted to unlink exhaust from hood with wrong exhaust id');
        }

        set(
          (state) => {
            state.project.data.hoods[hoodId].relations.exhaustId = undefined;
            state.project.data.exhausts[exhaustId].relations.hoodIds = state.project.data.exhausts[
              exhaustId
            ].relations.hoodIds.filter((hid) => hid !== hoodId);

            updateAssetModified(state.project.data.hoods[hoodId], state);
            updateAssetModified(state.project.data.exhausts[exhaustId], state);
          },
          undefined,
          { type: 'project/airflow/unlinkHoodExhaust', exhaustId, hoodId },
        );
      },
      createVav: async (linkToSupplyId, zoneId) => {
        const vavCount = Object.entries(get().project.data.vavs).length;
        const newVav = await get().plan.availableVAVs.get(`VAV ${vavCount + 1}`, 'vav-na');
        const user = get().planner.user.data?.userPrincipalName ?? '';
        newVav.airchefValues.datesAndAudit.author = user;
        newVav.airchefValues.datesAndAudit.modifiedBy = user;

        set(
          (state) => {
            state.project.data.vavs[newVav.id] = newVav;

            if (linkToSupplyId && state.project.data.supplies[linkToSupplyId]) {
              state.project.data.supplies[linkToSupplyId].relations.vavIds.push(newVav.id);
              state.project.data.vavs[newVav.id].relations.supplyId = linkToSupplyId;
              state.project.data.vavs[newVav.id].relations.zoneId = zoneId;
            }
          },
          undefined,
          { type: 'project/airflow/createVav', linkToSupplyId },
        );

        return get().project.data.vavs[newVav.id];
      },
      updateVav: (vav) => {
        if (!get().project.data.vavs[vav.id] || !vav) {
          throw new NoSuchAssetError('vav', 'Can not update VAV that does not exist');
        }
        set(
          (state) => {
            state.project.data.vavs[vav.id] = simpleClone(vav);

            updateAssetModified(state.project.data.vavs[vav.id], state);
          },
          undefined,
          { type: 'project/airflow/updateVav', vav },
        );
      },
      linkVavToSupply: (supplyId, vavId, zoneId) => {
        const { vavs, supplies } = get().project.data;

        const vav = vavs[vavId] as VAV | undefined;
        const supply = supplies[supplyId] as Supply | undefined;

        if (!vav) {
          throw new NoSuchAssetError('vav', 'Can not link nonexisting vav to supply');
        }

        if (!supply) {
          throw new NoSuchAssetError('supply', 'Can not link vav to nonexisting supply');
        }

        if (vav.relations.supplyId && vav.relations.supplyId !== supplyId) {
          throw new InvalidStateError('Vav is already linked to another supply!');
        }

        set(
          (state) => {
            state.project.data.vavs[vavId].relations.supplyId = supplyId;
            state.project.data.vavs[vavId].relations.zoneId = zoneId;
            if (!state.project.data.supplies[supplyId].relations.vavIds.includes(vavId)) {
              state.project.data.supplies[supplyId].relations.vavIds.push(vavId);
            }

            updateAssetModified(state.project.data.vavs[vavId], state);
            updateAssetModified(state.project.data.supplies[supplyId], state);
          },
          undefined,
          { type: 'project/airflow/linkVavToSupply', supplyId, vavId },
        );
      },
      unlinkVavSupply: (supplyId, vavId, deleteVav) => {
        const { vavs, supplies } = get().project.data;

        const vav = vavs[vavId] as VAV | undefined;
        const supply = supplies[supplyId] as Supply | undefined;

        if (!vav) {
          throw new NoSuchAssetError('vav', 'Can not unlink nonexisting vav from supply');
        }

        if (!supply) {
          throw new NoSuchAssetError('supply', 'Can not unlink vav from nonexisting supply');
        }

        if (vav.relations.supplyId !== supplyId) {
          throw new InvalidStateError('Attempted to unlink supply from vav with wrong supply id');
        }

        set(
          (state) => {
            state.project.data.vavs[vavId].relations.supplyId = undefined;
            state.project.data.supplies[supplyId].relations.vavIds = state.project.data.supplies[
              supplyId
            ].relations.vavIds.filter((vid) => vid !== vavId);

            updateAssetModified(state.project.data.vavs[vavId], state);
            updateAssetModified(state.project.data.supplies[supplyId], state);

            if (deleteVav) {
              const vav = state.project.data.vavs[vavId];

              for (const did of vav.relations.diffuserIds ?? []) {
                const diff = state.project.data.diffusers[did];
                if (!diff) continue;

                diff.relations.vavId = undefined;
              }

              delete state.project.data.vavs[vavId];
            }
          },
          undefined,
          { type: 'project/airflow/unlinkVavSupply', supplyId, vavId, deleteVav },
        );
      },
      linkDiffuserToSupply: (supplyId, diffuserId) => {
        const { diffusers, supplies } = get().project.data;

        const diffuser = diffusers[diffuserId] as Diffuser | undefined;
        const supply = supplies[supplyId] as Supply | undefined;

        if (!diffuser) {
          throw new NoSuchAssetError('diffuser', 'Can not link nonexisting diffuser to supply');
        }

        if (!supply) {
          throw new NoSuchAssetError('supply', 'Can not link diffuser to nonexisting supply');
        }

        if (diffuser.relations.supplyId && diffuser.relations.supplyId !== supplyId) {
          throw new InvalidStateError('Diffuser is already linked to another supply!');
        }

        set(
          (state) => {
            state.project.data.diffusers[diffuserId].relations.supplyId = supplyId;
            if (!state.project.data.supplies[supplyId].relations.diffuserIds.includes(diffuserId)) {
              state.project.data.supplies[supplyId].relations.diffuserIds.push(diffuserId);
            }

            updateAssetModified(state.project.data.diffusers[diffuserId], state);
            updateAssetModified(state.project.data.supplies[supplyId], state);
          },
          undefined,
          { type: 'project/airflow/linkDiffuserToSupply', supplyId, diffuserId },
        );
      },
      unlinkDiffuserSupply(supplyId, diffuserId) {
        const { diffusers, supplies } = get().project.data;

        const diffuser = diffusers[diffuserId] as Diffuser | undefined;
        const supply = supplies[supplyId] as Supply | undefined;

        if (!diffuser) {
          throw new NoSuchAssetError('vav', 'Can not unlink nonexisting diffuser from supply');
        }

        if (!supply) {
          throw new NoSuchAssetError('supply', 'Can not unlink diffuser from nonexisting supply');
        }

        if (diffuser.relations.supplyId !== supplyId) {
          throw new InvalidStateError('Attempted to unlink supply from diffuser with wrong supply id');
        }

        set(
          (state) => {
            state.project.data.diffusers[diffuserId].relations.supplyId = undefined;
            state.project.data.supplies[supplyId].relations.diffuserIds = state.project.data.supplies[
              supplyId
            ].relations.diffuserIds.filter((did) => did !== diffuserId);

            updateAssetModified(state.project.data.diffusers[diffuserId], state);
            updateAssetModified(state.project.data.supplies[supplyId], state);
          },
          undefined,
          { type: 'project/airflow/unlinkDiffuserSupply', supplyId, diffuserId },
        );
      },
      linkDiffuserToVav(vavId, diffuserId) {
        const { diffusers, vavs } = get().project.data;

        const diffuser = diffusers[diffuserId] as Diffuser | undefined;
        const vav = vavs[vavId] as VAV | undefined;

        if (!diffuser) {
          throw new NoSuchAssetError('diffuser', 'Can not link nonexisting diffuser to vav');
        }

        if (!vav) {
          throw new NoSuchAssetError('vav', 'Can not link diffuser to nonexisting vav');
        }

        if (diffuser.relations.vavId && diffuser.relations.vavId !== vavId) {
          throw new InvalidStateError('Diffuser is already linked to another vav!');
        }

        set(
          (state) => {
            state.project.data.diffusers[diffuserId].relations.vavId = vavId;
            if (!state.project.data.vavs[vavId].relations.diffuserIds) {
              state.project.data.vavs[vavId].relations.diffuserIds = [];
            }
            if (!state.project.data.vavs[vavId].relations.diffuserIds.includes(diffuserId)) {
              state.project.data.vavs[vavId].relations.diffuserIds.push(diffuserId);
            }

            updateAssetModified(state.project.data.diffusers[diffuserId], state);
            updateAssetModified(state.project.data.vavs[vavId], state);
          },
          undefined,
          { type: 'project/airflow/linkDiffuserToVav', vavId, diffuserId },
        );
      },
      unlinkDiffuserVav(vavId, diffuserId) {
        const { diffusers, vavs } = get().project.data;

        const diffuser = diffusers[diffuserId] as Diffuser | undefined;
        const vav = vavs[vavId] as VAV | undefined;

        if (!diffuser) {
          throw new NoSuchAssetError('diffuser', 'Can not unlink nonexisting diffuser from vav');
        }

        if (!vav) {
          throw new NoSuchAssetError('vav', 'Can not unlink diffuser from nonexisting vav');
        }

        if (diffuser.relations.vavId !== vavId) {
          throw new InvalidStateError('Attempted to unlink vav from diffuser with wrong vav id');
        }

        set(
          (state) => {
            state.project.data.diffusers[diffuserId].relations.vavId = undefined;
            state.project.data.vavs[vavId].relations.diffuserIds = (
              state.project.data.vavs[vavId].relations.diffuserIds ?? []
            ).filter((did) => did !== diffuserId);

            updateAssetModified(state.project.data.diffusers[diffuserId], state);
            updateAssetModified(state.project.data.vavs[vavId], state);
          },
          undefined,
          { type: 'project/airflow/unlinkDiffuserVav', vavId, diffuserId },
        );
      },
    },
  },
});

function getActionType(baseName: string, isPlan?: typeof planAction, planActionName?: string) {
  if (import.meta.env.DEV) {
    const base = `project/${baseName}`;
    if (isPlan === planAction) {
      return `plan/${planActionName ? `${planActionName}/` : ''}${base}`;
    }

    return base;
  }

  return baseName;
}

function getAirflowItemStore(itemName: AirflowItemName): 'supplies' | 'exhausts' | 'zones' | 'vavs' {
  switch (itemName) {
    case 'supply':
      return 'supplies';
    case 'exhaust':
      return 'exhausts';
    case 'zone':
      return 'zones';
    case 'vav':
      return 'vavs';
  }
}
export const entityStoreMap: EntityStoreMapping = {
  appliance: 'appliances',
  hood: 'hoods',
  exhaust: 'exhausts',
  room: 'rooms',
  zone: 'zones',
  vav: 'vavs',
  supply: 'supplies',
  group: 'groups',
  diffuser: 'diffusers',
};
